import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import styled from 'styled-components';
import { FixedSizeList as List } from 'react-window';
import BackToTopButton from 'components/BackToTopButton';

import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMoreMetaBooths,
  selectBoothPageInfo,
  selectTotalFilteredBoothsCount,
} from 'models/cms/booths';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import { useMediaQuery } from '@material-ui/core';
import HeaderComponent from './HeaderComponent';
import FooterWrapper from './FooterWrapper';
import ListRow from './ListRow';

const NoExhibitorHeaderComponentWrapper = styled.div`
  height: 100%;
  width: 100%;
  flex: 1 1 auto;
  display: flex;
  position: absolute;
`;

const NoExhibitorFooterWrapper = styled(FooterWrapper)`
  width: 100%;
  height: 90px;
  position: absolute;
`;

export const CMSFetchMoreIndicator = ({ offsetTop }: any) => {
  const dispatch = useDispatch();
  const pageInfo = useSelector(selectBoothPageInfo);

  useEffect(() => {
    if (pageInfo.fetching) return;
    dispatch(fetchMoreMetaBooths());
  }, [dispatch, pageInfo]);

  if (!pageInfo.fetching) return null;

  return (
    <NoExhibitorFooterWrapper offsetTop={offsetTop}>
      <CircularLoadingIndicator />
    </NoExhibitorFooterWrapper>
  );
};

const boothPreviewSize: {
  containerX: number;
  marginX: number;
  x: number;
  containerY: number;
  marginY: number;
  y: number;
} = {
  containerX: 330,
  marginX: 24,
  x: 330 + 24 * 2,
  containerY: 270,
  marginY: 20,
  y: 270 + 20 * 2,
};

const ListComponent = ({
  height,
  width,
  isFiltered,
  hasFilter,
  exhibitorIds,
}: {
  height: number;
  width: number;
  isFiltered: boolean;
  hasFilter: boolean;
  exhibitorIds: string[];
}) => {
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const totalFilteredBoothsCount = useSelector(selectTotalFilteredBoothsCount);

  const responsiveWidth: number = useMemo(() => (
    width >= boothPreviewSize.x ? boothPreviewSize.x : width
  ), [width]);

  const itemsPerRow: number = useMemo(() => (
    Math.floor(width / responsiveWidth)
  ), [width, responsiveWidth]);

  const rowCount: number = useMemo(() => (
    Math.ceil(exhibitorIds.length / itemsPerRow)
  ), [exhibitorIds.length, itemsPerRow]);

  const headerOffset: number = useMemo(() => {
    const noFilterBreadcrumbsTop = (
      !(isDesktop && hasFilter) ? 70 : 0
    );
    const filterResultsTop = ((isDesktop && isFiltered) ? 66 : 0);
    return filterResultsTop + noFilterBreadcrumbsTop;
  }, [isDesktop, isFiltered, hasFilter]);

  const containerHeight = height;

  const [scrollableTarget, setScrollableTarget] = useState();
  const scrollableTargetRef = useRef();

  useEffect(() => {
    setScrollableTarget(scrollableTargetRef.current);
  }, []);

  const wrapperRef = useRef({});
  wrapperRef.current = {
    headerOffset,
    itemsPerRow,
    hasFilter,
    containerHeight,
    rowCount,
    isFiltered,
    isDesktop,
  };

  const ListRowWrapper = useCallback((props: any) => (
    <ListRow {...props} {...wrapperRef.current} />
  ), []);

  if (isFiltered && !totalFilteredBoothsCount) {
    return (
      <NoExhibitorHeaderComponentWrapper>
        <HeaderComponent
          isFiltered={isFiltered}
          filterLength={totalFilteredBoothsCount}
          headerOffset={0}
          hasFilter={hasFilter}
        />
        <CMSFetchMoreIndicator offsetTop={0} />
      </NoExhibitorHeaderComponentWrapper>
    );
  }

  return (
    <>
      <List
        itemCount={rowCount}
        itemSize={boothPreviewSize.y}
        height={containerHeight}
        width={width}
        itemData={exhibitorIds}
        style={{ overflowX: 'hidden' }}
        outerRef={scrollableTargetRef}
      >
        {ListRowWrapper}
      </List>
      {scrollableTarget && <BackToTopButton scrollableTarget={scrollableTarget} />}
    </>
  );
};

export default ListComponent;
